import React, { useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { Link, useSearchParams } from "react-router-dom";
import { DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import {
  workOrderStatusMap,
} from "features/WorkOrders/constants/work-order.constants";
import WorkOrderLifecycleButtons from "features/WorkOrders/components/WorkOrderLifecycleButtons";
import { useWorkOrderOverview } from "./WorkOrderOverviewContext";
import OverviewTab from "./OverviewTab";
import TasksTab from "./TasksTab";
import {
  WorkOrderOverviewTabParam,
  isValidWorkOrderOverviewTabParam,
  searchParamForWorkOrderOverviewTabId,
  workOrderOverviewTabIdFromSearchParam,
} from "./overviewTabSearchParams";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { workOrderPermissionFlags } from "features/WorkOrders/utils/workOrderPermissions";
import MaterialRequestsTab from "./MaterialRequestsTab";
import { useProfile } from "features/Authentication/hooks/useProfile";
import { invalidateReadyForBillingWorkOrderCount } from "features/WorkOrders/workOrderReadyForBillingCountInvalidate";
import AttachmentsTab from "features/Attachments/components/AttachmentsTab";
import { AttachmentSourceType } from "features/Attachments/constants/attachment.constants";

export default function Section() {
  const { can } = useAccess();
  const { isSuperAdmin, user } = useProfile();
  const [searchParams, setSearchParams] = useSearchParams();
  const { workOrder, refetch } = useWorkOrderOverview();
  const canUpdateWo = can(Permissions.WORK_ORDER.UPDATE);
  const canViewMaterialRequests = can(Permissions.MATERIAL_REQUEST.READ);
  const tabQuery = searchParams.get("tab");

  useEffect(() => {
    if (tabQuery && !isValidWorkOrderOverviewTabParam(tabQuery)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("tab");
          return next;
        },
        { replace: true },
      );
    }
  }, [tabQuery, setSearchParams]);

  if (!workOrder) {
    return (
      <Row>
        <Col lg={12}>
          <p className="text-muted mb-0">
            This work order could not be loaded.
          </p>
        </Col>
      </Row>
    );
  }

  const displayTitle = workOrder.title?.trim() || workOrder.code || "—";
  const codeLine =
    workOrder.code && workOrder.code !== displayTitle ? workOrder.code : null;

  const activeTab = workOrderOverviewTabIdFromSearchParam(tabQuery);

  const woFlags = workOrderPermissionFlags(user, workOrder, isSuperAdmin);

  const setTabInUrl = (tabId: string) => {
    const param = searchParamForWorkOrderOverviewTabId(tabId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!param || param === WorkOrderOverviewTabParam.OVERVIEW) {
          next.delete("tab");
        } else {
          next.set("tab", param);
        }
        return next;
      },
      { replace: true },
    );
  };

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <Card className="mt-n4 mx-n4">
            <div className="bg-light-subtle">
              <CardBody className="pb-0 px-4">
                <Row className="mb-3 align-items-start">
                  <div className="col-md">
                    <div>
                      <h4 className="fw-bold">{displayTitle}</h4>
                      {codeLine ? (
                        <p className="text-muted mb-1">
                          <span className="fw-medium">{codeLine}</span>
                        </p>
                      ) : null}
                      <div className="hstack gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted">Status:</span>
                          <StatusBadge
                            value={workOrder.status}
                            map={workOrderStatusMap}
                          />
                        </div>
                        {workOrder.created_at ? (
                          <>
                            <div className="vr d-none d-sm-block" />
                            <div className="text-muted">
                              Created:{" "}
                              <span className="text-body fw-medium">
                                <DateTime date={workOrder.created_at} />
                              </span>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-auto mt-3 mt-md-0">
                    <div className="hstack gap-2 flex-wrap justify-content-md-end">
                      {canUpdateWo &&
                      (woFlags.canEdit || woFlags.canEditLineItems) ? (
                        <Link
                          to={`/work-orders/edit/${workOrder.id}`}
                          className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1 flex-shrink-0"
                        >
                          <i className="ri-pencil-line align-bottom" />
                          Edit
                        </Link>
                      ) : null}
                      <WorkOrderLifecycleButtons
                        workOrder={workOrder}
                        onSuccess={() => {
                          void refetch();
                          invalidateReadyForBillingWorkOrderCount();
                        }}
                      />
                    </div>
                  </div>
                </Row>

                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "1" },
                        "text-body fw-semibold",
                      )}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("1");
                      }}
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "2" },
                        "text-body fw-semibold",
                      )}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("2");
                      }}
                    >
                      Tasks
                    </NavLink>
                  </NavItem>
                  {canViewMaterialRequests ? (
                    <NavItem>
                      <NavLink
                        className={classnames(
                          { active: activeTab === "3" },
                          "text-body fw-semibold",
                        )}
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setTabInUrl("3");
                        }}
                      >
                        Material Requests
                      </NavLink>
                    </NavItem>
                  ) : null}
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "4" },
                        "text-body fw-semibold",
                      )}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("4");
                      }}
                    >
                      Attachments
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <TabContent activeTab={activeTab} className="text-muted">
            <TabPane tabId="1">
              <OverviewTab />
            </TabPane>
            <TabPane tabId="2">
              <TasksTab />
            </TabPane>
            {canViewMaterialRequests ? (
              <TabPane tabId="3">
                <MaterialRequestsTab />
              </TabPane>
            ) : null}
            <TabPane tabId="4">
              <AttachmentsTab
                sourceType={AttachmentSourceType.WORK_ORDER}
                sourceId={workOrder.id}
                canManage={canUpdateWo}
              />
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </React.Fragment>
  );
}
